import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { AccountTree as AccountTreeIcon } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { Box, Checkbox, FormControlLabel, FormGroup, IconButton, Paper, Stack, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import cytoscape from "cytoscape";
import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import WordMeaning from "../../../admin/pzl_common/WordMeaning";
import { usePagesTranslation } from "../../locales";
import { DEFAULT_THRESHOLDS, LAYOUT, STYLE } from "./WordNetworkConstants";

/** Same highlight / dim as node tap (Word of the Day initial selection). */
export function applyNodeHighlightVisual(cy, node) {
    if (!cy || !node || node.length === 0) return;
    cy.elements().removeClass("highlighted");
    cy.elements().removeClass("dimmed");
    cy.elements().removeClass("connected");
    const allConnectedEdges = node.connectedEdges();
    const visibleConnectedEdges = allConnectedEdges.filter((edge) => edge.style("display") === "element");
    const connectedNodes = visibleConnectedEdges.connectedNodes().not(node);
    node.addClass("highlighted");
    visibleConnectedEdges.addClass("highlighted");
    connectedNodes.addClass("connected");
    const elementsToKeep = cy.collection([node]).merge(visibleConnectedEdges).merge(connectedNodes);
    cy.elements().not(elementsToKeep).addClass("dimmed");
}

const WordNetworkPanel = ({
    wordNetwork,
    onToggle,
    enableDelete = false,
    onDeleteWord,
    onNodeSelect,
    showWordMeaning = true,
    onRefresh,
    hideVisibilityToggle = false,
    hideMostConnected = false,
    hideNodeEdgeCount = false,
    hideInteractionHint = false,
    /** Minimal graph only (e.g. Word of the Day): no header, filters, or Paper chrome. */
    embedMode = false,
    /** After layout, highlight the node whose label matches (case-insensitive). */
    initialHighlightWord = null,
}) => {
    const { t, lang: uiLang } = usePagesTranslation();
    const theme = useTheme();
    const isMobileViewport = useMediaQuery(theme.breakpoints.down("md"));
    const [selectedWeightTypes, setSelectedWeightTypes] = useState(() => {
        return ["syntacticDependency", "semanticSimilarity"];
    }); // Array of selected weight types
    const [selectedNode, setSelectedNode] = useState(null); // Track selected node for deletion
    const [showOnlyNonConnected, setShowOnlyNonConnected] = useState(false); // Toggle to show only non-connected nodes
    const [selectedWordForMeaning, setSelectedWordForMeaning] = useState(null); // Track selected word for meaning display

    // Individual thresholds for each weight type
    const [thresholds] = useState({
        syntacticDependency: DEFAULT_THRESHOLDS.syntacticDependency,
        semanticSimilarity: DEFAULT_THRESHOLDS.semanticSimilarity,
    });

    // Color mapping for each weight type
    const WEIGHT_TYPE_COLORS = {
        syntacticDependency: "#4a90e2",      // Blue
        semanticSimilarity: "#ff6b6b",       // Red
    };

    const cyRef = useRef(null);
    const containerRef = useRef(null);
    const layoutRef = useRef(null);
    const isDestroyedRef = useRef(false);
    const initialHighlightWordRef = useRef(initialHighlightWord);
    useEffect(() => {
        initialHighlightWordRef.current = initialHighlightWord;
    }, [initialHighlightWord]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;
        let roRaf = null;
        const ro = new ResizeObserver(() => {
            if (roRaf != null) cancelAnimationFrame(roRaf);
            roRaf = requestAnimationFrame(() => {
                roRaf = null;
                if (cyRef.current) {
                    try {
                        cyRef.current.resize();
                    } catch {
                        /* ignore */
                    }
                }
            });
        });
        ro.observe(el);
        return () => {
            if (roRaf != null) cancelAnimationFrame(roRaf);
            ro.disconnect();
        };
    }, [wordNetwork]);

    // Helper function to check if a link passes the threshold filters
    const linkPassesThreshold = (link) => {
        for (const weightType of selectedWeightTypes) {
            const typeThreshold = thresholds[weightType] || 0;
            let value = 0;

            if (weightType === "syntacticDependency") {
                value = link.syntacticDependency || 0;
            } else if (weightType === "semanticSimilarity") {
                value = link.semanticSimilarity !== undefined ? link.semanticSimilarity : 0;
            }

            if (value > typeThreshold) {
                return true;
            }
        }
        return false;
    };

    const convertToCytoscapeElements = () => {
        if (!wordNetwork || !wordNetwork.nodes || !wordNetwork.links) return [];

        const elements = [];

        // Calculate connection counts for each node based on links that pass threshold filters
        // (normalize IDs to strings for consistent comparison)
        // Only calculate if hideMostConnected is false
        let mostConnectedNodeId = null;
        if (!hideMostConnected) {
            const connectionCounts = {};
            wordNetwork.links.forEach((link) => {
                // Only count links that pass the threshold filters
                if (linkPassesThreshold(link)) {
                    const sourceId = String(link.source);
                    const targetId = String(link.target);
                    connectionCounts[sourceId] = (connectionCounts[sourceId] || 0) + 1;
                    connectionCounts[targetId] = (connectionCounts[targetId] || 0) + 1;
                }
            });

            // Find the node(s) with maximum connections
            const connectionValues = Object.values(connectionCounts);
            const maxConnections = connectionValues.length > 0 ? Math.max(...connectionValues) : 0;
            const nodesWithMaxConnections = Object.keys(connectionCounts).filter(
                nodeId => connectionCounts[nodeId] === maxConnections
            );

            // Select one node if multiple have the same max (use the first one)
            // Normalize to string for comparison
            mostConnectedNodeId = nodesWithMaxConnections.length > 0 ? String(nodesWithMaxConnections[0]) : null;
        }

        // Add nodes
        wordNetwork.nodes.forEach((node) => {
            const label = node.label || node.id;
            const labelLength = label.length;
            const nodeWidth = Math.max(60, labelLength * 15 + 10);
            const nodeHeight = Math.max(60, Math.ceil(labelLength / 8) * 26 + 8);
            // Normalize node.id to string for comparison
            const isMostConnected = mostConnectedNodeId !== null && String(node.id) === mostConnectedNodeId;

            elements.push({
                data: {
                    id: node.id,
                    label: label,
                    width: nodeWidth,
                    height: nodeHeight,
                    isMostConnected: isMostConnected,
                },
            });
        });

        // Add all edges (we'll filter and color them in the update effect)
        wordNetwork.links.forEach((link, index) => {
            elements.push({
                data: {
                    id: `edge-${index}`,
                    source: link.source,
                    target: link.target,
                    syntacticDependency: link.syntacticDependency || 0,
                    semanticSimilarity: link.semanticSimilarity !== undefined ? link.semanticSimilarity : 0,
                    displayWeight: 1,
                    color: "#a0a0a0",
                },
            });
        });

        return elements;
    };

    useEffect(() => {
        if (!wordNetwork || !wordNetwork.nodes || !wordNetwork.links || !containerRef.current) {
            return;
        }

        // If cytoscape instance doesn't exist, create it
        if (!cyRef.current) {
            const elements = convertToCytoscapeElements();
            if (elements.length === 0) return;

            cyRef.current = cytoscape({
                container: containerRef.current,
                elements: elements,
                style: STYLE,
                userPanningEnabled: true,
                userZoomingEnabled: true,
                boxSelectionEnabled: false,
            });

            const cy = cyRef.current;
            isDestroyedRef.current = false;

            // Lock nodes during layout to prevent movement
            cy.nodes().lock();

            // Mark the most connected node with a special class (only if not hidden)
            if (!hideMostConnected) {
                cy.nodes().forEach((node) => {
                    if (node.data("isMostConnected")) {
                        node.addClass("most-connected");
                    }
                });
            }

            // Start layout manually for better control
            layoutRef.current = cy.layout(LAYOUT);

            // Stop layout when it completes and unlock nodes for dragging
            layoutRef.current.one('layoutstop', () => {
                if (layoutRef.current && cyRef.current) {
                    const cyInstance = cyRef.current;
                    // Unlock nodes after layout so they can be dragged
                    cyInstance.nodes().unlock();
                    cyInstance.nodes().grabify();
                    try {
                        cyInstance.resize();
                    } catch {
                        /* ignore */
                    }
                    const hi = initialHighlightWordRef.current;
                    if (hi) {
                        const lc = String(hi).trim().toLowerCase();
                        const match = cyInstance.nodes().filter((n) => String(n.data("label")).toLowerCase() === lc);
                        if (match.length > 0) {
                            applyNodeHighlightVisual(cyInstance, match[0]);
                        }
                    }
                    layoutRef.current = null;
                }
            });

            layoutRef.current.run();

            // Immediately apply filters to set edge colors
            if (wordNetwork.links) {
                wordNetwork.links.forEach((link, index) => {
                    const edge = cy.getElementById(`edge-${index}`);
                    if (edge.length === 0) return;

                    let shouldShow = false;
                    let edgeColor = "#a0a0a0";
                    let matchedType = null;

                    for (const weightType of selectedWeightTypes) {
                        const typeThreshold = thresholds[weightType] || 0;
                        let value = 0;

                        if (weightType === "syntacticDependency") {
                            value = link.syntacticDependency || 0;
                        } else if (weightType === "semanticSimilarity") {
                            value = link.semanticSimilarity !== undefined ? link.semanticSimilarity : 0;
                        }

                        if (value > typeThreshold) {
                            shouldShow = true;
                            if (!matchedType) {
                                matchedType = weightType;
                                edgeColor = WEIGHT_TYPE_COLORS[weightType];
                            }
                        }
                    }

                    edge.data("color", edgeColor);
                    if (shouldShow) {
                        edge.style("display", "element");
                    } else {
                        edge.style("display", "none");
                    }
                });

                // Recalculate most connected node based on visible edges (only if not hidden)
                if (!hideMostConnected) {
                    const connectionCounts = {};
                    cy.edges().forEach((edge) => {
                        // Only count visible edges
                        if (edge.style("display") === "element") {
                            const sourceId = String(edge.source().id());
                            const targetId = String(edge.target().id());
                            connectionCounts[sourceId] = (connectionCounts[sourceId] || 0) + 1;
                            connectionCounts[targetId] = (connectionCounts[targetId] || 0) + 1;
                        }
                    });

                    // Find the node(s) with maximum connections
                    const connectionValues = Object.values(connectionCounts);
                    const maxConnections = connectionValues.length > 0 ? Math.max(...connectionValues) : 0;
                    const nodesWithMaxConnections = Object.keys(connectionCounts).filter(
                        nodeId => connectionCounts[nodeId] === maxConnections
                    );
                    const mostConnectedNodeId = nodesWithMaxConnections.length > 0 ? String(nodesWithMaxConnections[0]) : null;

                    // Update most-connected class on nodes
                    cy.nodes().forEach((node) => {
                        const nodeId = String(node.id());
                        if (mostConnectedNodeId !== null && nodeId === mostConnectedNodeId) {
                            node.addClass("most-connected");
                        } else {
                            node.removeClass("most-connected");
                        }
                    });
                } else {
                    // Remove most-connected class from all nodes if hidden
                    cy.nodes().forEach((node) => {
                        node.removeClass("most-connected");
                    });
                }

                cy.style().update();
            }
        }

        // Always update event handlers when enableDelete changes
        const cy = cyRef.current;
        if (!cy) return;

        // Remove existing handlers
        cy.off("tap", "node");
        cy.off("tap");

        // Add click handler for nodes to highlight node and its connected edges
        const nodeTapHandler = function (evt) {
            if (isDestroyedRef.current || !cyRef.current) return;

            // Stop any running layout before handling click
            if (layoutRef.current) {
                try {
                    layoutRef.current.stop();
                    layoutRef.current = null;
                } catch (e) {
                    // Layout might already be stopped
                }
            }

            const node = evt.target;
            const nodeLabel = node.data("label");

            // Store selected node for deletion if enabled
            if (enableDelete) {
                setSelectedNode({
                    id: node.id(),
                    label: nodeLabel
                });
            }

            // Notify parent component about node selection
            if (onNodeSelect && nodeLabel) {
                onNodeSelect(nodeLabel);
            }

            // Set selected word for meaning display
            setSelectedWordForMeaning(nodeLabel);

            applyNodeHighlightVisual(cy, node);
        };

        // Click on background to clear highlights
        const backgroundTapHandler = function (evt) {
            if (isDestroyedRef.current || !cyRef.current) return;

            if (evt.target === cy) {
                cy.elements().removeClass("highlighted");
                cy.elements().removeClass("dimmed");
                cy.elements().removeClass("connected");
                // Clear selected node when clicking background
                if (enableDelete) {
                    setSelectedNode(null);
                }
                // Notify parent to clear network word selection
                if (onNodeSelect) {
                    onNodeSelect(null);
                }
                // Clear selected word for meaning display
                setSelectedWordForMeaning(null);
            }
        };

        cy.on("tap", "node", nodeTapHandler);
        cy.on("tap", backgroundTapHandler);

        return () => {
            isDestroyedRef.current = true;

            if (cyRef.current) {
                const cyInstance = cyRef.current;

                // Stop layout if it's running
                if (layoutRef.current) {
                    try {
                        const layout = layoutRef.current;
                        layout.stop();
                        layoutRef.current = null;
                    } catch (e) {
                        layoutRef.current = null;
                    }
                }

                // Remove event handlers
                try {
                    cyInstance.off("tap", "node");
                    cyInstance.off("tap");
                } catch (e) {
                    // Handlers might already be removed
                }

                // Destroy Cytoscape instance
                try {
                    cyInstance.off();
                    cyInstance.destroy();
                } catch (e) {
                    // Instance might already be destroyed
                }

                cyRef.current = null;
            }
        };
    }, [wordNetwork, enableDelete, selectedWeightTypes, thresholds, hideMostConnected, embedMode]);

    // Re-apply initial highlight after edge filters change (embed / Word of the Day).
    useEffect(() => {
        if (!embedMode || !initialHighlightWord || !cyRef.current || isDestroyedRef.current) return;
        const cy = cyRef.current;
        const lc = String(initialHighlightWord).trim().toLowerCase();
        const m = cy.nodes().filter((n) => String(n.data("label")).toLowerCase() === lc);
        if (m.length > 0) applyNodeHighlightVisual(cy, m[0]);
    }, [embedMode, initialHighlightWord, wordNetwork, selectedWeightTypes]);

    // Update edge visibility and colors when selectedWeightTypes or thresholds change
    useEffect(() => {
        if (!wordNetwork || !wordNetwork.links || !cyRef.current) return;

        const cy = cyRef.current;

        // Update all edges based on current filters
        wordNetwork.links.forEach((link, index) => {
            const edge = cy.getElementById(`edge-${index}`);
            if (edge.length === 0) return;

            let shouldShow = false;
            let displayWeight = 1;
            let edgeColor = "#a0a0a0";
            let matchedType = null;

            // Check each selected weight type
            for (const weightType of selectedWeightTypes) {
                const typeThreshold = thresholds[weightType] || 0;
                let value = 0;

                if (weightType === "syntacticDependency") {
                    value = link.syntacticDependency || 0;
                } else if (weightType === "semanticSimilarity") {
                    value = link.semanticSimilarity !== undefined ? link.semanticSimilarity : 0;
                }

                if (value > typeThreshold) {
                    shouldShow = true;
                    if (!matchedType) {
                        matchedType = weightType;
                        edgeColor = WEIGHT_TYPE_COLORS[weightType];
                        displayWeight = value || 1;
                    }
                }
            }

            // Update edge visibility and styling
            if (shouldShow) {
                edge.style("display", "element");
                edge.data("displayWeight", displayWeight);
                edge.data("color", edgeColor);
            } else {
                edge.style("display", "none");
                // Ensure color is set even when hidden to avoid Cytoscape warnings
                edge.data("color", edgeColor);
            }
        });

        // Recalculate most connected node based on visible edges (only if not hidden)
        if (!hideMostConnected) {
            const connectionCounts = {};
            cy.edges().forEach((edge) => {
                // Only count visible edges
                if (edge.style("display") === "element") {
                    const sourceId = String(edge.source().id());
                    const targetId = String(edge.target().id());
                    connectionCounts[sourceId] = (connectionCounts[sourceId] || 0) + 1;
                    connectionCounts[targetId] = (connectionCounts[targetId] || 0) + 1;
                }
            });

            // Find the node(s) with maximum connections
            const connectionValues = Object.values(connectionCounts);
            const maxConnections = connectionValues.length > 0 ? Math.max(...connectionValues) : 0;
            const nodesWithMaxConnections = Object.keys(connectionCounts).filter(
                nodeId => connectionCounts[nodeId] === maxConnections
            );
            const mostConnectedNodeId = nodesWithMaxConnections.length > 0 ? String(nodesWithMaxConnections[0]) : null;

            // Update most-connected class on nodes
            cy.nodes().forEach((node) => {
                const nodeId = String(node.id());
                if (mostConnectedNodeId !== null && nodeId === mostConnectedNodeId) {
                    node.addClass("most-connected");
                } else {
                    node.removeClass("most-connected");
                }
            });
        } else {
            // Remove most-connected class from all nodes if hidden
            cy.nodes().forEach((node) => {
                node.removeClass("most-connected");
            });
        }

        // Update styles to reflect color changes
        cy.style().update();

        // Ensure nodes remain grabbable after any updates
        cy.nodes().grabify();
    }, [wordNetwork, selectedWeightTypes, thresholds, hideMostConnected]);

    // Update node visibility based on showOnlyNonConnected toggle
    useEffect(() => {
        if (!wordNetwork || !wordNetwork.links || !cyRef.current) return;

        const cy = cyRef.current;

        if (showOnlyNonConnected) {
            // First, determine which nodes should be visible (those with no visible edges)
            // We need to check edge visibility based on checkbox options
            const nodesToShow = new Set();

            cy.nodes().forEach((node) => {
                // Get all edges connected to this node
                const connectedEdges = node.connectedEdges();

                // Check if any connected edge is visible (matches current filter options)
                // We need to check the actual edge data against current filter options
                let hasVisibleEdge = false;

                connectedEdges.forEach((edge) => {
                    // Check if this edge should be visible based on checkbox options
                    const edgeIndex = parseInt(edge.id().replace('edge-', ''));
                    if (edgeIndex >= 0 && edgeIndex < wordNetwork.links.length) {
                        const link = wordNetwork.links[edgeIndex];
                        let shouldShow = false;

                        for (const weightType of selectedWeightTypes) {
                            const typeThreshold = thresholds[weightType] || 0;
                            let value = 0;

                            if (weightType === "syntacticDependency") {
                                value = link.syntacticDependency || 0;
                            } else if (weightType === "semanticSimilarity") {
                                value = link.semanticSimilarity !== undefined ? link.semanticSimilarity : 0;
                            }

                            if (value > typeThreshold) {
                                shouldShow = true;
                                break;
                            }
                        }

                        if (shouldShow) {
                            hasVisibleEdge = true;
                        }
                    }
                });

                // Only show nodes that have no visible edges
                if (!hasVisibleEdge) {
                    nodesToShow.add(node.id());
                }
            });

            // Update node visibility
            cy.nodes().forEach((node) => {
                if (nodesToShow.has(node.id())) {
                    node.style("display", "element");
                } else {
                    node.style("display", "none");
                }
            });

            // Hide edges connected to hidden nodes for cleaner visualization
            cy.edges().forEach((edge) => {
                const sourceId = edge.source().id();
                const targetId = edge.target().id();
                const wasVisible = edge.style("display") === "element";

                if (!nodesToShow.has(sourceId) || !nodesToShow.has(targetId)) {
                    // Hide edge if either node is hidden
                    edge.style("display", "none");
                } else if (wasVisible) {
                    // Restore edge visibility if both nodes are visible and edge was originally visible
                    // The first useEffect will have set the correct visibility based on checkboxes
                    // We just need to make sure it's visible if it should be
                    const edgeIndex = parseInt(edge.id().replace('edge-', ''));
                    if (edgeIndex >= 0 && edgeIndex < wordNetwork.links.length) {
                        const link = wordNetwork.links[edgeIndex];
                        let shouldShow = false;

                        for (const weightType of selectedWeightTypes) {
                            const typeThreshold = thresholds[weightType] || 0;
                            let value = 0;

                            if (weightType === "syntacticDependency") {
                                value = link.syntacticDependency || 0;
                            } else if (weightType === "semanticSimilarity") {
                                value = link.semanticSimilarity !== undefined ? link.semanticSimilarity : 0;
                            }

                            if (value > typeThreshold) {
                                shouldShow = true;
                                break;
                            }
                        }

                        if (shouldShow) {
                            edge.style("display", "element");
                        }
                    }
                }
            });
        } else {
            // Show all nodes
            cy.nodes().forEach((node) => {
                node.style("display", "element");
            });

            // Restore edge visibility based on checkbox options
            wordNetwork.links.forEach((link, index) => {
                const edge = cy.getElementById(`edge-${index}`);
                if (edge.length === 0) return;

                let shouldShow = false;
                let edgeColor = "#a0a0a0";

                for (const weightType of selectedWeightTypes) {
                    const typeThreshold = thresholds[weightType] || 0;
                    let value = 0;

                    if (weightType === "syntacticDependency") {
                        value = link.syntacticDependency || 0;
                    } else if (weightType === "semanticSimilarity") {
                        value = link.semanticSimilarity !== undefined ? link.semanticSimilarity : 0;
                    }

                    if (value > typeThreshold) {
                        shouldShow = true;
                        edgeColor = WEIGHT_TYPE_COLORS[weightType];
                        break;
                    }
                }

                if (shouldShow) {
                    edge.style("display", "element");
                    edge.data("color", edgeColor);
                } else {
                    edge.style("display", "none");
                    edge.data("color", edgeColor);
                }
            });
        }

        // Recalculate most connected node based on visible edges (only if not hidden)
        if (!hideMostConnected) {
            const connectionCounts = {};
            cy.edges().forEach((edge) => {
                // Only count visible edges
                if (edge.style("display") === "element") {
                    const sourceId = String(edge.source().id());
                    const targetId = String(edge.target().id());
                    connectionCounts[sourceId] = (connectionCounts[sourceId] || 0) + 1;
                    connectionCounts[targetId] = (connectionCounts[targetId] || 0) + 1;
                }
            });

            // Find the node(s) with maximum connections
            const connectionValues = Object.values(connectionCounts);
            const maxConnections = connectionValues.length > 0 ? Math.max(...connectionValues) : 0;
            const nodesWithMaxConnections = Object.keys(connectionCounts).filter(
                nodeId => connectionCounts[nodeId] === maxConnections
            );
            const mostConnectedNodeId = nodesWithMaxConnections.length > 0 ? String(nodesWithMaxConnections[0]) : null;

            // Update most-connected class on nodes
            cy.nodes().forEach((node) => {
                const nodeId = String(node.id());
                if (mostConnectedNodeId !== null && nodeId === mostConnectedNodeId) {
                    node.addClass("most-connected");
                } else {
                    node.removeClass("most-connected");
                }
            });
        } else {
            // Remove most-connected class from all nodes if hidden
            cy.nodes().forEach((node) => {
                node.removeClass("most-connected");
            });
        }

        cy.style().update();

        // Ensure nodes remain grabbable after any updates
        cy.nodes().grabify();
    }, [wordNetwork, showOnlyNonConnected, selectedWeightTypes, thresholds, hideMostConnected]);

    /** Zoom toward viewport center (helps touch devices where wheel zoom is awkward). */
    const zoomGraphByFactor = useCallback((factor) => {
        const cy = cyRef.current;
        if (!cy) return;
        const w = cy.width();
        const h = cy.height();
        if (!w || !h) return;
        const z = cy.zoom();
        const next = Math.min(Math.max(z * factor, cy.minZoom()), cy.maxZoom());
        try {
            cy.zoom({
                level: next,
                renderedPosition: { x: w / 2, y: h / 2 },
            });
        } catch {
            /* ignore */
        }
    }, []);

    if (!wordNetwork || !wordNetwork.nodes || !wordNetwork.links) {
        return null;
    }

    if (embedMode) {
        return (
            <Box
                sx={{
                    width: "100%",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        px: 1,
                        py: 0.75,
                        bgcolor: "background.paper",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <FormGroup sx={{ display: "flex", flexDirection: "row", gap: 0.5, alignItems: "center", flexWrap: "wrap" }}>
                        <Tooltip title={t("puzzle.wordNetwork.syntactic-tooltip")} arrow>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={selectedWeightTypes.includes("syntacticDependency")}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedWeightTypes([...selectedWeightTypes, "syntacticDependency"]);
                                            } else {
                                                const next = selectedWeightTypes.filter((type) => type !== "syntacticDependency");
                                                if (next.length > 0) setSelectedWeightTypes(next);
                                            }
                                        }}
                                        sx={{
                                            color: WEIGHT_TYPE_COLORS.syntacticDependency,
                                            "&.Mui-checked": {
                                                color: WEIGHT_TYPE_COLORS.syntacticDependency,
                                            },
                                            padding: "4px",
                                        }}
                                    />
                                }
                                label={t("puzzle.wordNetwork.syntactic-label")}
                                sx={{
                                    margin: 0,
                                    "& .MuiFormControlLabel-label": {
                                        fontSize: "0.75rem",
                                    },
                                }}
                            />
                        </Tooltip>
                        <Tooltip title={t("puzzle.wordNetwork.semantic-tooltip")} arrow>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={selectedWeightTypes.includes("semanticSimilarity")}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedWeightTypes([...selectedWeightTypes, "semanticSimilarity"]);
                                            } else {
                                                const next = selectedWeightTypes.filter((type) => type !== "semanticSimilarity");
                                                if (next.length > 0) setSelectedWeightTypes(next);
                                            }
                                        }}
                                        sx={{
                                            color: WEIGHT_TYPE_COLORS.semanticSimilarity,
                                            "&.Mui-checked": {
                                                color: WEIGHT_TYPE_COLORS.semanticSimilarity,
                                            },
                                            padding: "4px",
                                        }}
                                    />
                                }
                                label={t("puzzle.wordNetwork.semantic-label")}
                                sx={{
                                    margin: 0,
                                    "& .MuiFormControlLabel-label": {
                                        fontSize: "0.75rem",
                                    },
                                }}
                            />
                        </Tooltip>
                    </FormGroup>
                </Box>
                <Box sx={{ position: "relative", width: "100%" }}>
                    <Box
                        ref={containerRef}
                        sx={{
                            width: "100%",
                            height: { xs: 320, sm: 400 },
                            minHeight: 280,
                            overflow: "hidden",
                            bgcolor: "#fafafa",
                        }}
                    />
                    <Stack
                        direction="column"
                        spacing={0.25}
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            zIndex: 2,
                            bgcolor: "background.paper",
                            borderRadius: 1,
                            boxShadow: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            p: 0.25,
                        }}
                    >
                        <Tooltip title={t("puzzle.wordNetwork.zoom-in")} arrow placement="left">
                            <span>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    aria-label={t("puzzle.wordNetwork.zoom-in")}
                                    onClick={() => zoomGraphByFactor(1.2)}
                                >
                                    <ZoomInIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={t("puzzle.wordNetwork.zoom-out")} arrow placement="left">
                            <span>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    aria-label={t("puzzle.wordNetwork.zoom-out")}
                                    onClick={() => zoomGraphByFactor(1 / 1.2)}
                                >
                                    <ZoomOutIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Box>
            </Box>
        );
    }

    return (
        <Paper
            elevation={3}
            sx={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                height: '100%',
                maxHeight: '100vh',
                backgroundColor: '#fff',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
        >
            {/* Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                borderBottom: '1px solid #e0e0e0'
            }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3BA666' }}>
                    Word Network
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {wordNetwork.metadata && !hideNodeEdgeCount && (
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            {wordNetwork.metadata.nodeCount} nodes | {wordNetwork.metadata.edgeCount} edges
                        </Typography>
                    )}
                    {onToggle && (
                        <Tooltip title="Back to Words List" arrow>
                            <IconButton
                                onClick={onToggle}
                                size="small"
                                sx={{
                                    color: '#666',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        color: '#333'
                                    }
                                }}
                            >
                                <ArrowBackIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* Checkboxes */}
            <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
                <FormGroup sx={{ display: "flex", flexDirection: "row", gap: 0.5, alignItems: 'center' }}>
                    <Tooltip title="Show words connected by grammatical relationships (how words work together in sentences)">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={selectedWeightTypes.includes("syntacticDependency")}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedWeightTypes([...selectedWeightTypes, "syntacticDependency"]);
                                        } else {
                                            setSelectedWeightTypes(selectedWeightTypes.filter(type => type !== "syntacticDependency"));
                                        }
                                    }}
                                    sx={{
                                        color: WEIGHT_TYPE_COLORS.syntacticDependency,
                                        "&.Mui-checked": {
                                            color: WEIGHT_TYPE_COLORS.syntacticDependency,
                                        },
                                        padding: "4px",
                                    }}
                                />
                            }
                            label="Syntactic Dependency"
                            sx={{
                                margin: 0,
                                "& .MuiFormControlLabel-label": {
                                    fontSize: "0.75rem",
                                }
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="Show words with similar meanings (words that are related in concept or definition)">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={selectedWeightTypes.includes("semanticSimilarity")}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedWeightTypes([...selectedWeightTypes, "semanticSimilarity"]);
                                        } else {
                                            setSelectedWeightTypes(selectedWeightTypes.filter(type => type !== "semanticSimilarity"));
                                        }
                                    }}
                                    sx={{
                                        color: WEIGHT_TYPE_COLORS.semanticSimilarity,
                                        "&.Mui-checked": {
                                            color: WEIGHT_TYPE_COLORS.semanticSimilarity,
                                        },
                                        padding: "4px",
                                    }}
                                />
                            }
                            label="Semantic Similarity"
                            sx={{
                                margin: 0,
                                "& .MuiFormControlLabel-label": {
                                    fontSize: "0.75rem",
                                }
                            }}
                        />
                    </Tooltip>
                    {enableDelete && onRefresh && (
                        <Tooltip title="Refresh word network" arrow>
                            <IconButton
                                onClick={onRefresh}
                                size="small"
                                sx={{
                                    color: '#666',
                                    ml: 'auto',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        color: '#333'
                                    }
                                }}
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {!hideVisibilityToggle && (
                        <Tooltip title={showOnlyNonConnected ? "Show all nodes" : "Show only non-connected nodes"} arrow>
                            <IconButton
                                onClick={() => setShowOnlyNonConnected(!showOnlyNonConnected)}
                                size="small"
                                sx={{
                                    color: showOnlyNonConnected ? '#1976d2' : '#666',
                                    ml: (enableDelete && onRefresh) ? 0 : 'auto',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        color: showOnlyNonConnected ? '#1565c0' : '#333'
                                    }
                                }}
                            >
                                {showOnlyNonConnected ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    )}
                    {enableDelete && (
                        <>
                            {selectedNode && (
                                <Tooltip title={`Delete word "${selectedNode.label}"`} arrow>
                                    <Button
                                        onClick={() => {
                                            if (onDeleteWord && selectedNode) {
                                                onDeleteWord(selectedNode.label);
                                                setSelectedNode(null);
                                                // Clear highlights
                                                if (cyRef.current) {
                                                    cyRef.current.elements().removeClass("highlighted");
                                                    cyRef.current.elements().removeClass("dimmed");
                                                    cyRef.current.elements().removeClass("connected");
                                                }
                                            }
                                        }}
                                        size="small"
                                        variant="outlined"
                                        startIcon={<DeleteIcon />}
                                        sx={{
                                            color: '#d32f2f',
                                            borderColor: '#d32f2f',
                                            textTransform: 'none',
                                            '&:hover': {
                                                backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                                borderColor: '#b71c1c',
                                                color: '#b71c1c'
                                            }
                                        }}
                                    >
                                        Delete
                                    </Button>
                                </Tooltip>
                            )}
                        </>
                    )}
                </FormGroup>
            </Box>

            {/* Network Graph and Word Meaning Container */}
            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0,
                minWidth: 0,
                width: '100%',
                alignSelf: 'stretch',
            }}>
                {/* Network Graph — full width of panel; 50% height only when word meaning is open */}
                <Box
                    sx={{
                        flex: (showWordMeaning && selectedWordForMeaning) ? '0 1 50%' : 1,
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        alignSelf: 'stretch',
                        minHeight: (showWordMeaning && selectedWordForMeaning) ? 300 : 400,
                        maxHeight: (showWordMeaning && selectedWordForMeaning) ? '50%' : 'none',
                        border: 'none',
                        borderRadius: 0,
                        overflow: 'hidden',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        ref={containerRef}
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            width: '100%',
                            overflow: 'hidden',
                            position: 'relative',
                        }}
                    />
                    <Stack
                        direction="column"
                        spacing={0.25}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 2,
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            boxShadow: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            p: 0.25,
                        }}
                    >
                        <Tooltip title={t('puzzle.wordNetwork.zoom-in')} arrow placement="left">
                            <span>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    aria-label={t('puzzle.wordNetwork.zoom-in')}
                                    onClick={() => zoomGraphByFactor(1.2)}
                                >
                                    <ZoomInIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={t('puzzle.wordNetwork.zoom-out')} arrow placement="left">
                            <span>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    aria-label={t('puzzle.wordNetwork.zoom-out')}
                                    onClick={() => zoomGraphByFactor(1 / 1.2)}
                                >
                                    <ZoomOutIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Box>

                {/* Interaction Instructions (desktop: scroll-wheel hint; omit on small screens / touch) */}
                {!hideInteractionHint && !isMobileViewport && (
                    <Box sx={{
                        p: 1.5,
                        borderTop: '1px solid #e0e0e0',
                        backgroundColor: '#f8f9fa',
                        textAlign: 'center'
                    }}>
                        <Typography
                            variant="caption"
                            sx={{
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                                display: 'block',
                                lineHeight: 1.5
                            }}
                        >
                            <strong>Interactions:</strong> Zoom in/out with scroll wheel • Click and drag nodes to move them • Click a node to show its links
                        </Typography>
                    </Box>
                )}

                {/* Word Meaning */}
                {showWordMeaning && selectedWordForMeaning && (
                    <Box sx={{
                        borderTop: '1px solid #e0e0e0',
                        p: 0,
                        overflow: 'hidden',
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <WordMeaning
                            selectedWord={selectedWordForMeaning}
                            onSaveDefinition={null}
                            showClueButton={false}
                            fullHeight={true}
                            uiLang={uiLang}
                        />
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

export default WordNetworkPanel;

